import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Download, Sparkles, Printer } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Link } from 'react-router-dom';
import logger from '../utils/logger';

const LABELS = {
  inspiration: 'Inspiration',
  details: 'About this piece',
  message: 'A little message',
};

/**
 * Admin panel for the three printable A6 keepsake cards that ship with each
 * bracelet. Lives inside the Product Admin edit dialog.
 */
export default function KeepsakeCardsSection({ productId, apiBase, backendUrl }) {
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const absolute = (path) => (path?.startsWith('/api/') ? `${backendUrl}${path}` : path);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${apiBase}/products/${productId}/keepsake-cards`);
        if (cancelled) return;
        setCards(response.data?.cards || null);
      } catch (error) {
        if (cancelled) return;
        logger.error('Could not load keepsake cards:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, apiBase]);

  const handleRegenerate = async () => {
    if (regenerating) return;
    try {
      setRegenerating(true);
      const response = await axios.post(
        `${apiBase}/products/${productId}/keepsake-cards/regenerate`,
        {},
        { timeout: 60000 },
      );
      setCards(response.data?.cards || null);
      toast.success('Keepsake cards refreshed');
    } catch (error) {
      logger.error('Card regeneration failed:', error);
      toast.error(error.response?.data?.detail || 'Could not regenerate cards');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      className="space-y-3 p-4 bg-[#F5F5F0] rounded-sm border border-[#CAD2C5]/40"
      data-testid="keepsake-cards-section"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Label className="text-[#354F52] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            Keepsake cards
          </Label>
          <p className="text-xs text-[#84A98C] mt-1 max-w-md">
            Three printable A6 cards (inspiration, details, message) auto-built
            from this product&apos;s copy. Tuck them in with the bracelet when you ship.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            asChild
            data-testid="open-keepsake-print-btn"
          >
            <Link
              to={`/admin/products/${productId}/print-keepsake-cards`}
              className="flex items-center gap-2"
            >
              <Printer className="w-4 h-4" strokeWidth={1.5} />
              Print cards
            </Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            disabled={regenerating || loading}
            className="flex items-center gap-2"
            data-testid="regenerate-keepsake-cards-btn"
          >
            {regenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                Refreshing…
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                Refresh cards
              </>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-[#84A98C] italic">Loading cards…</p>
      ) : !cards ? (
        <p className="text-xs text-[#84A98C] italic">No cards yet — click &quot;Refresh cards&quot;.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Object.entries(LABELS).map(([key, label]) => {
            const url = cards[key];
            if (!url) return null;
            const absUrl = absolute(url);
            return (
              <div
                key={key}
                className="bg-white rounded-sm border border-[#CAD2C5]/60 p-3 flex flex-col items-center"
                data-testid={`keepsake-card-${key}`}
              >
                <a
                  href={absUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full aspect-square overflow-hidden rounded-sm border border-[#CAD2C5]/40 bg-white"
                  title={`Open ${label} card`}
                >
                  <img
                    src={absUrl}
                    alt={label}
                    className="w-full h-full object-contain"
                  />
                </a>
                <p className="text-caption text-[#52796F] mt-2">{label}</p>
                <a
                  href={absUrl}
                  download={`keepsake-${productId}-${key}.png`}
                  className="text-xs text-[#D4AF37] hover:text-[#B8941F] mt-1 inline-flex items-center gap-1"
                  data-testid={`download-keepsake-${key}`}
                >
                  <Download className="w-3 h-3" strokeWidth={1.5} />
                  Download
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
